import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";

const TOTAL_PAGES = 21;
const pages = Array.from({ length: TOTAL_PAGES }, (_, i) => `/graphic-design/page-${String(i + 1).padStart(2, "0")}.jpg`);

export default function GraphicDesign() {
  const [lightbox, setLightbox] = useState(null); // page index (1-based)

  const next = () => setLightbox((p) => (p < TOTAL_PAGES ? p + 1 : 1));
  const prev = () => setLightbox((p) => (p > 1 ? p - 1 : TOTAL_PAGES));

  return (
    <section
      id="graphic-design"
      data-testid="graphic-design-section"
      className="relative bg-[#0B0E14] text-[#F2EBE5] py-24 md:py-36"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-20">
          <div>
            <div className="flex items-center gap-3 text-[10px] tracking-overline uppercase text-[#D18A58] mb-5">
              <span className="h-px w-10 bg-[#D18A58]" />
              Graphic Design · 04
            </div>
            <h2 className="font-display text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight">
              Graphic Design <span className="italic text-[#D18A58]">for Fashion</span>.
            </h2>
            <p className="mt-5 text-[#C5A29B] text-sm">By Saanvi Gupte · 2025 · {TOTAL_PAGES} pages</p>
          </div>
          <p className="max-w-md text-sm md:text-base text-[#C5A29B] leading-relaxed">
            A complete graphic design portfolio for fashion — catalogue, social media, magazine, product packaging and more. Click any page to view it full-screen.
          </p>
        </div>

        <div className="space-y-6 md:space-y-8">
          {pages.map((src, i) => (
            <motion.button
              key={i}
              data-testid={`gd-page-${i + 1}`}
              type="button"
              onClick={() => setLightbox(i + 1)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
              whileHover={{ y: -4 }}
              className="group relative block w-full overflow-hidden rounded-sm bg-[#12141D] border border-white/5 cursor-zoom-in"
            >
              <img
                src={src}
                alt={`Graphic Design — page ${i + 1}`}
                loading="lazy"
                className="block w-full h-auto"
              />
              <div className="absolute top-4 left-4 text-[10px] uppercase tracking-overline text-[#F2EBE5]/80 bg-[#0B0E14]/60 backdrop-blur px-3 py-1 rounded-full border border-white/10">
                Page {String(i + 1).padStart(2, "0")} / {TOTAL_PAGES}
              </div>
              <div className="absolute top-4 right-4 h-10 w-10 rounded-full bg-[#0B0E14]/60 backdrop-blur border border-white/10 flex items-center justify-center text-[#F2EBE5] opacity-0 group-hover:opacity-100 transition-opacity">
                <Maximize2 className="h-4 w-4" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gd-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setLightbox(null);
              if (e.key === "ArrowRight") next();
              if (e.key === "ArrowLeft") prev();
            }}
            tabIndex={-1}
            className="fixed inset-0 z-[80] bg-[#0B0E14]/95 backdrop-blur-2xl flex items-center justify-center p-4 md:p-10"
          >
            <button
              data-testid="gd-close"
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              className="absolute top-5 right-5 z-10 h-11 w-11 rounded-full bg-[#D18A58] text-[#0B0E14] flex items-center justify-center hover:bg-[#F2EBE5]"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              data-testid="gd-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-[#F2EBE5]/10 border border-white/15 backdrop-blur text-[#F2EBE5] flex items-center justify-center hover:bg-[#D18A58] hover:text-[#0B0E14]"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              data-testid="gd-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-[#F2EBE5]/10 border border-white/15 backdrop-blur text-[#F2EBE5] flex items-center justify-center hover:bg-[#D18A58] hover:text-[#0B0E14]"
              aria-label="Next page"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            <motion.img
              key={lightbox}
              src={pages[lightbox - 1]}
              alt={`Graphic Design — page ${lightbox}`}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-[95vw] max-h-[90vh] w-auto h-auto object-contain rounded-sm shadow-2xl"
            />

            <div className="absolute bottom-5 left-0 right-0 text-center text-[10px] uppercase tracking-overline text-[#F2EBE5]/70">
              Page {String(lightbox).padStart(2, "0")} of {TOTAL_PAGES} · Esc to close
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
